import React, { useEffect, useMemo, useState } from 'react';
import { API } from '../../api/client';

interface Category {
  id: string;
  type: string;
  l1: string;
  l2: string;
  l3: string;
  definition: string | null;
  inUse: number;
}

const TYPES = ['Technological', 'Functional'];

const emptyDraft = () => ({ type: TYPES[0], l1: '', l2: '', l3: '', definition: '' });

export default function AdminPriorities() {
  const [cats, setCats] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // id of the row being edited inline, plus its working copy
  const [editId, setEditId] = useState<string>('');
  const [editDraft, setEditDraft] = useState(emptyDraft());

  // add-new form
  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState(emptyDraft());

  const load = () => {
    setLoading(true);
    API.getPriorityCategories()
      .then(data => { if (data) setCats(data.categories); })
      .catch(() => setError('Failed to load the priority taxonomy.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // L1 -> L2 -> [rows], preserving the backend's sorted order.
  const tree = useMemo(() => {
    const l1map = new Map<string, Map<string, Category[]>>();
    cats.forEach(c => {
      if (!l1map.has(c.l1)) l1map.set(c.l1, new Map());
      const l2map = l1map.get(c.l1)!;
      if (!l2map.has(c.l2)) l2map.set(c.l2, []);
      l2map.get(c.l2)!.push(c);
    });
    return l1map;
  }, [cats]);

  const l1Options = useMemo(() => Array.from(new Set(cats.map(c => c.l1))), [cats]);
  const l2Options = useMemo(() => Array.from(new Set(cats.map(c => c.l2))), [cats]);

  const startEdit = (c: Category) => {
    setError('');
    setEditId(c.id);
    setEditDraft({ type: c.type, l1: c.l1, l2: c.l2, l3: c.l3, definition: c.definition ?? '' });
  };

  const saveEdit = async () => {
    if (!editDraft.l1.trim() || !editDraft.l2.trim() || !editDraft.l3.trim()) {
      setError('L1, L2 and L3 names are all required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await API.updatePriorityCategory(editId, editDraft);
      setEditId('');
      load();
    } catch {
      setError('Failed to save the priority.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c: Category) => {
    if (!window.confirm(`Delete "${c.l3}" (${c.id})? This cannot be undone.`)) return;
    setBusy(true);
    setError('');
    try {
      await API.deletePriorityCategory(c.id);
      load();
    } catch (e: any) {
      setError(
        String(e?.message).includes('409')
          ? `"${c.l3}" is in use by an FI and can't be deleted. Clear it from those FIs first.`
          : 'Failed to delete the priority.'
      );
    } finally {
      setBusy(false);
    }
  };

  // Rename an L1 or L2 group across every row under it (denormalized taxonomy).
  const renameGroup = async (level: 'l1' | 'l2', rows: Category[], current: string) => {
    const next = window.prompt(`Rename ${level.toUpperCase()} group "${current}" — applies to ${rows.length} priorit${rows.length === 1 ? 'y' : 'ies'}:`, current);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === current) return;
    setBusy(true);
    setError('');
    try {
      for (const c of rows) {
        await API.updatePriorityCategory(c.id, {
          type: c.type, l1: c.l1, l2: c.l2, l3: c.l3, definition: c.definition ?? '',
          [level]: trimmed,
        });
      }
      load();
    } catch {
      setError(`Failed to rename the ${level.toUpperCase()} group.`);
    } finally {
      setBusy(false);
    }
  };

  const addRow = async () => {
    if (!addDraft.l1.trim() || !addDraft.l2.trim() || !addDraft.l3.trim()) {
      setError('L1, L2 and L3 names are all required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await API.createPriorityCategory(addDraft);
      setAdding(false);
      setAddDraft(emptyDraft());
      load();
    } catch {
      setError('Failed to add the priority.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="phil-loading">Loading…</div>;

  const fieldStyle: React.CSSProperties = { padding: '5px 8px', fontSize: 12, border: '1px solid #ccc', borderRadius: 4 };

  return (
    <>
      <div className="phil-page-heading">Priority Taxonomy</div>
      <div className="phil-page-sub">
        The master list of priorities (L1 → L2 → L3) used across FI priority calls, scoring and reports.
        Per-FI priority weights are set on each FI's post-call review.
      </div>

      {error && (
        <div className="phil-error" style={{ marginTop: 12 }}>
          {error}
          <button style={{ marginLeft: 12, fontSize: 11, cursor: 'pointer', background: 'none', border: 'none', color: '#c0392b', textDecoration: 'underline' }} onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      <div className="phil-card" style={{ marginTop: 20 }}>
        <div className="phil-card-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2>Priorities</h2>
            <span style={{ fontSize: 12, color: '#888' }}>{cats.length} priorities</span>
          </div>
          <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={() => { setError(''); setAdding(a => !a); }}>
            {adding ? 'Cancel' : '+ Add priority'}
          </button>
        </div>

        {adding && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '12px 18px', background: '#f5f7fa', borderBottom: '1px solid #eee' }}>
            <select style={{ ...fieldStyle, width: 130 }} value={addDraft.type} onChange={e => setAddDraft({ ...addDraft, type: e.target.value })}>
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input list="l1opts" style={{ ...fieldStyle, width: 150 }} placeholder="L1 domain" value={addDraft.l1} onChange={e => setAddDraft({ ...addDraft, l1: e.target.value })} />
            <input list="l2opts" style={{ ...fieldStyle, width: 170 }} placeholder="L2 group" value={addDraft.l2} onChange={e => setAddDraft({ ...addDraft, l2: e.target.value })} />
            <input style={{ ...fieldStyle, width: 200 }} placeholder="L3 priority name" value={addDraft.l3} onChange={e => setAddDraft({ ...addDraft, l3: e.target.value })} />
            <input style={{ ...fieldStyle, flex: 1, minWidth: 200 }} placeholder="Definition (optional)" value={addDraft.definition} onChange={e => setAddDraft({ ...addDraft, definition: e.target.value })} />
            <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={addRow} disabled={busy}>Add</button>
          </div>
        )}

        <datalist id="l1opts">{l1Options.map(o => <option key={o} value={o} />)}</datalist>
        <datalist id="l2opts">{l2Options.map(o => <option key={o} value={o} />)}</datalist>

        <div style={{ padding: '8px 0 0' }}>
          {Array.from(tree.entries()).map(([l1, l2map]) => {
            const l1rows = Array.from(l2map.values()).flat();
            return (
              <div key={l1} style={{ marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#1A2744', color: '#fff', padding: '7px 18px', fontSize: 13, fontWeight: 700 }}>
                  <span>{l1}</span>
                  <button title="Rename L1 group" onClick={() => renameGroup('l1', l1rows, l1)} disabled={busy} style={{ background: 'none', border: 'none', color: '#9fc5ff', cursor: 'pointer', fontSize: 11 }}>✎</button>
                </div>
                {Array.from(l2map.entries()).map(([l2, rows]) => (
                  <div key={l2}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f5f7fa', padding: '6px 18px', fontSize: 12, fontWeight: 700, color: '#1A2744', borderTop: '1px solid #eee' }}>
                      <span>{l2}</span>
                      <span style={{ color: '#aaa', fontWeight: 400 }}>· {rows[0].type}</span>
                      <button title="Rename L2 group" onClick={() => renameGroup('l2', rows, l2)} disabled={busy} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 11 }}>✎</button>
                    </div>
                    {rows.map(c => editId === c.id ? (
                      <div key={c.id} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '8px 18px', background: '#fffdf3', borderBottom: '1px solid #f4f4f4' }}>
                        <select style={{ ...fieldStyle, width: 130 }} value={editDraft.type} onChange={e => setEditDraft({ ...editDraft, type: e.target.value })}>
                          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <input list="l1opts" style={{ ...fieldStyle, width: 150 }} value={editDraft.l1} onChange={e => setEditDraft({ ...editDraft, l1: e.target.value })} />
                        <input list="l2opts" style={{ ...fieldStyle, width: 170 }} value={editDraft.l2} onChange={e => setEditDraft({ ...editDraft, l2: e.target.value })} />
                        <input style={{ ...fieldStyle, width: 200 }} value={editDraft.l3} onChange={e => setEditDraft({ ...editDraft, l3: e.target.value })} />
                        <input style={{ ...fieldStyle, flex: 1, minWidth: 200 }} placeholder="Definition" value={editDraft.definition} onChange={e => setEditDraft({ ...editDraft, definition: e.target.value })} />
                        <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={saveEdit} disabled={busy}>Save</button>
                        <button className="phil-btn phil-btn-sm" onClick={() => setEditId('')} disabled={busy}>Cancel</button>
                      </div>
                    ) : (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '7px 18px', borderBottom: '1px solid #f4f4f4' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: '#333' }} title={c.id}>{c.l3}</div>
                          {c.definition && <div style={{ fontSize: 11, color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.definition}</div>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          {c.inUse > 0 && <span style={{ fontSize: 10, color: '#aaa' }}>{c.inUse} in use</span>}
                          <button className="phil-btn phil-btn-sm" onClick={() => startEdit(c)}>Edit</button>
                          <button
                            className="phil-btn phil-btn-sm"
                            onClick={() => remove(c)}
                            disabled={c.inUse > 0 || busy}
                            title={c.inUse > 0 ? 'In use by an FI — clear those selections first' : 'Delete'}
                            style={{ color: c.inUse > 0 ? '#ccc' : '#c0392b' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
